import React, { useEffect, useState } from 'react';
import styled from 'styled-components/native';
import Svg, { Circle } from 'react-native-svg';
import { View } from 'react-native';
import { useWindowDimensions } from 'react-native';
import { B, P, H5 } from './Typography';
import Colors from '../constants/Colors';

const StyledStudyJourneyChartView = styled.View`
  width: 100%;
  height: 100%;
  align-items: center;
  justify-content: center;
`;

const StyledChartView = styled.View<{ size: number }>`
  align-self: center;
  align-items: center;
  width: ${p => p.size}px;
  height: ${p => p.size}px;
  position: absolute;
`;

const StyledCircleProgress = styled.View<{ color: string }>`
  width: 23px;
  height: 23px;
  border-radius: 11.5px;
  background-color: white;
  border: 3px solid ${p => (p.color ? p.color : '')};
`;

const StyledProgressView = styled.View<{ size: number; strokeWidth: number }>`
  width: ${p => p.size + p.strokeWidth / 2}px;
  height: ${p => p.size + p.strokeWidth / 2}px;
  align-self: center;
  justify-content: center;
  align-items: flex-end;
  position: absolute;
  z-index: 1;
`;

const StyledSegmentTextContainer = styled.View<{ width?: number }>`
  position: absolute;
  ${p => (p.width ? `max-width: ${p.width}px;` : '')}
`;

const StyledSegmentTitle = styled.Text<{ right?: boolean; margin?: number }>`
  font-family: 'DMSans-Bold';
  font-size: 12px;
  line-height: 16px;
  letter-spacing: 0px;
  color: #3a2e9e;
  margin-bottom: 4px;
  ${p => (p.right ? 'text-align: right;' : '')}
  margin-right: ${p => (p.right ? (p.margin ? p.margin : 0) : 0)}px;
  margin-left: ${p => (p.right ? 0 : p.margin ? p.margin : 0)}px;
`;

// const StyledSegmentDescription = styled.Text<{
//   right?: boolean;
//   margin?: number;
// }>`
//   font-family: 'WorkSans-Regular';
//   font-size: 12px;
//   line-height: 14px;
//   letter-spacing: 0px;
//   color: #5b4fc2;
//   ${p => (p.right ? 'text-align: right;' : '')}
//   padding-right: ${p => (p.right ? (p.margin ? p.margin : 0) : 0)}px;
//   padding-left: ${p => (p.right ? 0 : p.margin ? p.margin : 0)}px;
// `;

const StyledLinkText = styled(P)`
  font-size: 14px;
  line-height: 16px;
  text-decoration-line: underline;
  color: ${Colors.primary};
`;

const StyledTouchableOpacity = styled.TouchableOpacity`
  top: 8px;
`;

const StyledChartDayText = styled(B)`
  font-size: 24px;
  line-height: 28px;
  letter-spacing: 0px;
  text-align: center;
  color: ${Colors.primary};
`;

const StyledChartTextView = styled.View`
  width: 100%;
  height: 100%;
  position: absolute;
  justify-content: center;
  align-items: center;
`;

const StyledChartTitle = styled(H5)`
  text-align: center;
  margin-bottom: 340px;
  color: ${Colors.primary};
  z-index: 1;
`;

const StyledImage = styled.Image`
  width: 346px;
  height: 104px;
  position: absolute;
`;

interface StudyJourneyChartData {
  title: string;
  description: string;
  dayCount: number;
}

interface SegmentTextPosition {
  angle: number;
  x: number;
  y: number;
  topMargin: number;
  bottomMargin: number;
}

export interface StudyJourneyChartProps {
  title: string;
  data: StudyJourneyChartData[];
  currentDay: number;
  onLearnMore?: () => void;
}

const colorArray = [
  '#CCF5FF',
  '#FFCAAA',
  '#887CEB',
  '#EDE27E',
  '#887CEB',
  '#EDE27E',
  '#FFCAAA',
  '#887CEB',
  '#FFCAAA',
  '#887CEB',
  '#EDE27E',
  '#887CEB',
  '#EDE27E',
  '#FFCAAA',
  '#887CEB'
];

const StudyJourneyChart: React.FC<StudyJourneyChartProps> = ({
  title = '',
  data = [],
  currentDay = 0,
  onLearnMore
}) => {
  const window = useWindowDimensions();
  const [startAngles, setStartAngles] = useState<number[]>([]);
  const [segmentTextPositions, setSegmentTextPositions] = useState<
    SegmentTextPosition[]
  >([]);
  const [segmentTextSizes, setSegmentTextSizes] = useState<
    { height: number; width: number; index: number }[]
  >([]);
  const strokeWidth = 15;
  const size = 200;
  const center = size / 2;
  const radius = (size - strokeWidth) / 2;
  const circumference = 2 * Math.PI * radius;
  const chartStartAngle = -90;

  const getTotalDays = () => {
    let total = 0;
    data.forEach(data => {
      total += data.dayCount;
    });
    return total;
  };

  const getProgress = () => {
    const step = 360 / getTotalDays();
    return step * currentDay;
  };

  const getCurrentSegmentIndex = () => {
    let day = 0;
    let currentIndex: number | undefined;
    for (let i = 0; i < data.length; i++) {
      day += data[i].dayCount;
      if (day >= currentDay) {
        currentIndex = i;
        break;
      }
    }
    if (currentIndex) {
      return currentIndex;
    }
    return 0;
  };

  const getSegmentTextPosition = (angle: number) => {
    const x = radius * Math.cos((angle + chartStartAngle) * (Math.PI / 180));
    const y = radius * Math.sin((angle + chartStartAngle) * (Math.PI / 180));
    return {
      angle: angle,
      x: x,
      y: y,
      topMargin: 0,
      bottomMargin: 0
    };
  };

  const getCurrentSegmentDayText = () => {
    let totalUntilCurrentSegment = 0;
    for (let i = 0; i < getCurrentSegmentIndex(); i++) {
      totalUntilCurrentSegment += data[i].dayCount;
    }
    return `Day ${currentDay - totalUntilCurrentSegment}/${
      data[getCurrentSegmentIndex()].dayCount
    }`;
  };

  const getWidth = (angle: number) => {
    if (angle >= 45 && angle <= (window.width <= 320 ? 160 : 135)) {
      return window.width < 380 ? (window.width <= 320 ? 50 : 85) : 95;
    } else if (angle >= (window.width <= 320 ? 200 : 240) && angle <= 315) {
      return window.width < 380 ? (window.width <= 320 ? 50 : 95) : 90;
    }
    return 150;
  };

  useEffect(() => {
    const totalDays = getTotalDays();
    let angle = 0;
    const angles: number[] = [];
    data.forEach(item => {
      angles.push(angle);
      angle += (item.dayCount / totalDays) * 360;
    });
    setStartAngles(angles);
    angles.push(360);
    const textPositions: SegmentTextPosition[] = [];
    for (let i = 0; i < angles.length - 1; i++) {
      textPositions.push(
        getSegmentTextPosition((angles[i + 1] - angles[i]) / 2 + angles[i])
      );
    }
    setSegmentTextPositions(textPositions);
  }, []);

  const segmentTextContainerTransform = (index: number) => {
    const marginHorizontal = window.width <= 320 ? 18 : 22;
    const marginVertical = 10;
    const width = segmentTextSizes.find(i => i.index === index)?.width;
    const height = segmentTextSizes.find(i => i.index === index)?.height;
    if (width && height) {
      // center upper or lower segments vertically
      if (
        segmentTextPositions[index].angle >= 320 ||
        segmentTextPositions[index].angle <= 40 ||
        (segmentTextPositions[index].angle <= 230 &&
          segmentTextPositions[index].angle >= 130)
      ) {
        return {
          transform: [
            {
              translateX:
                segmentTextPositions[index].x +
                (segmentTextPositions[index].angle < 180
                  ? width / 2
                  : -width / 2)
            },
            {
              translateY:
                segmentTextPositions[index].angle <= 230 &&
                segmentTextPositions[index].angle >= 130
                  ? marginVertical + segmentTextPositions[index].y + height / 2
                  : -marginVertical + segmentTextPositions[index].y - height / 2
            }
          ]
        };
      }
      // center other segments horizontally
      return {
        transform: [
          {
            translateX:
              segmentTextPositions[index].x +
              (segmentTextPositions[index].angle < 180
                ? width / 2 + marginHorizontal
                : -width / 2 - marginHorizontal)
          },
          { translateY: segmentTextPositions[index].y }
        ]
      };
    }
    return {
      transform: [
        {
          translateX: segmentTextPositions[index].x
        },
        { translateY: segmentTextPositions[index].y }
      ]
    };
  };

  return (
    <StyledStudyJourneyChartView>
      <StyledChartTitle>{title}</StyledChartTitle>
      <StyledProgressView
        size={size}
        strokeWidth={strokeWidth}
        style={{
          transform: [{ rotate: getProgress() + chartStartAngle + 'deg' }]
        }}
      >
        <StyledCircleProgress color={colorArray[getCurrentSegmentIndex()]} />
      </StyledProgressView>
      {segmentTextPositions.length > 0 &&
        data.map((item, index) => (
          <StyledSegmentTextContainer
            width={getWidth(segmentTextPositions[index].angle)}
            onLayout={event => {
              const layout = event.nativeEvent.layout;
              const sizes = segmentTextSizes;
              sizes.push({
                width: layout.width,
                height: layout.height,
                index: index
              });
              setSegmentTextSizes([...sizes]);
            }}
            key={`${index}-segment-text`}
            style={segmentTextContainerTransform(index)}
          >
            <View>
              <StyledSegmentTitle
                right={segmentTextPositions[index].angle > 180}
              >
                {item.title}
              </StyledSegmentTitle>
            </View>
            {/* <StyledSegmentTextView>
              <StyledSegmentDescription
                right={segmentTextPositions[index].angle > 180}
                margin={segmentTextPositions[index].bottomMargin}
              >
                {item.description}
              </StyledSegmentDescription>
            </StyledSegmentTextView> */}
          </StyledSegmentTextContainer>
        ))}
      <StyledChartTextView>
        <StyledChartDayText>{getCurrentSegmentDayText()}</StyledChartDayText>
      </StyledChartTextView>

      <StyledChartView size={size}>
        <StyledImage
          source={require('../assets/study-journey-chart/chart-arrow.png')}
          style={{ top: -100 }}
        />

        <StyledImage
          source={require('../assets/study-journey-chart/chart-arrow.png')}
          style={{
            transform: [{ rotate: '116deg' }, { scale: 0.85 }],
            top: 115,
            left: 80
          }}
        />
        <StyledImage
          source={require('../assets/study-journey-chart/chart-arrow.png')}
          style={{
            transform: [{ rotate: '245deg' }, { scale: 0.8 }],
            top: 130,
            right: 70
          }}
        />
        <Svg viewBox={`0 0 ${size} ${size}`}>
          {data.map((item, index) => (
            <Circle
              fillOpacity={0}
              key={`${index}-segment`}
              cx={center}
              cy={center}
              r={radius}
              strokeWidth={strokeWidth}
              stroke={colorArray[index]}
              strokeDashoffset={
                circumference * (1 - item.dayCount / getTotalDays())
              }
              strokeDasharray={circumference}
              originX={center}
              originY={center}
              rotation={startAngles[index] + chartStartAngle}
            />
          ))}
        </Svg>
      </StyledChartView>
      <StyledTouchableOpacity
        onPress={() => {
          if (onLearnMore) {
            onLearnMore();
          }
        }}
      >
        <StyledLinkText>Learn More</StyledLinkText>
      </StyledTouchableOpacity>
    </StyledStudyJourneyChartView>
  );
};

export default StudyJourneyChart;
